import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/api';
import { getDateRange, fmt$, fmtDate, s } from './reportKit';

// ── Dashboard Tab ──
export default function BusinessDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { start, end } = getDateRange(range);
      const result = await adminApi.getDashboard({ startdate: start, enddate: end });
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={s.sub}>Loading dashboard data...</p>;
  if (error) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <p style={s.errorText}>{error}</p>
      <button style={{ ...s.btnPrimary, marginTop: '1rem' }} onClick={load}>Retry</button>
    </div>
  );
  if (!data) return null;

  const { summary, ordersByDay, topProducts, topCustomers, recentOrders } = data;
  const maxDayOrders = Math.max(...ordersByDay.map(d => d.orders), 1);

  return (
    <div>
      {/* Date range selector */}
      <div style={s.rangeBar}>
        {['7d', '30d', '90d', 'ytd'].map(r => (
          <button key={r} style={range === r ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setRange(r)}>
            {r === 'ytd' ? 'YTD' : r}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div style={s.kpiGrid}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total Orders</div>
          <div style={s.kpiValue}>{summary.totalOrders}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Revenue</div>
          <div style={s.kpiValue}>{fmt$(summary.totalRevenue)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Unique Customers</div>
          <div style={s.kpiValue}>{summary.uniqueCustomers}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Avg Order Value</div>
          <div style={s.kpiValue}>{fmt$(summary.avgOrderValue)}</div>
        </div>
      </div>

      {/* Order trend chart */}
      {ordersByDay.length > 0 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Order Trend</h3>
          <div style={s.chartContainer}>
            {ordersByDay.map((d, i) => (
              <div key={i} style={s.chartCol}>
                <div style={s.chartBarWrap}>
                  <div style={{ ...s.chartBar, height: `${(d.orders / maxDayOrders) * 100}%` }} title={`${d.date}: ${d.orders} orders, ${fmt$(d.revenue)}`} />
                </div>
                {ordersByDay.length <= 31 && <div style={s.chartLabel}>{d.date.slice(5)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Products + Top Customers side by side */}
      <div style={s.twoCol}>
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Top Products (by qty)</h3>
          <table style={s.table}>
            <thead><tr><th style={s.th}>#</th><th style={s.th}>Product</th><th style={s.thR}>Qty</th><th style={s.thR}>Revenue</th></tr></thead>
            <tbody>
              {topProducts.map((p, i) => (
                <tr key={i}><td style={s.td}>{i + 1}</td><td style={s.td}>{p.itemName}</td><td style={s.tdR}>{p.totalQty}</td><td style={s.tdR}>{fmt$(p.totalRevenue)}</td></tr>
              ))}
              {topProducts.length === 0 && <tr><td colSpan={4} style={s.td}>No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>Top Customers (by revenue)</h3>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Customer</th><th style={s.thR}>Orders</th><th style={s.thR}>Revenue</th><th style={s.thR}>Last Order</th></tr></thead>
            <tbody>
              {topCustomers.map((c, i) => (
                <tr key={i}><td style={s.td}>{c.customerName}</td><td style={s.tdR}>{c.orderCount}</td><td style={s.tdR}>{fmt$(c.totalRevenue)}</td><td style={s.tdR}>{fmtDate(c.lastOrderDate)}</td></tr>
              ))}
              {topCustomers.length === 0 && <tr><td colSpan={4} style={s.td}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent orders */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>Recent Orders</h3>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Order #</th><th style={s.th}>Date</th><th style={s.th}>Customer</th><th style={s.thR}>Total</th></tr></thead>
          <tbody>
            {recentOrders.map((o, i) => (
              <tr key={i}><td style={s.td}>{o.number}</td><td style={s.td}>{fmtDate(o.date)}</td><td style={s.td}>{o.customerName}</td><td style={s.tdR}>{fmt$(o.total)}</td></tr>
            ))}
            {recentOrders.length === 0 && <tr><td colSpan={4} style={s.td}>No orders</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
